"use client";

import { Facebook, Instagram, Loader, Minus, Plus, Twitter, Youtube } from "lucide-react";
import { useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border border-border p-5">
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">
          § STATUS / REGISTERED
        </p>
        <p className="font-default text-lg text-foreground mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="h-10 px-4 font-default text-xs uppercase tracking-[0.18em] border-[1.5px] rounded-md">
          Add Another
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="first_name" render={({
      field
    }) => <FormItem>
            <FormControl>
              <Input placeholder="Name (optional)" {...field} className="h-12 bg-transparent border-0 border-b-[1.5px] border-border px-0 font-default text-base text-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary" />
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />
      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormControl>
              <Input type="email" placeholder="dev@codehub.io" {...field} className="h-12 bg-transparent border-0 border-b-[1.5px] border-border px-0 font-default text-base text-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-primary" />
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />
      <FormField control={control} name="consent" rules={{
      required: "You must agree to join"
    }} render={({
      field
    }) => <FormItem>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={!!field.value} onChange={e => field.onChange(e.target.checked)} className="mt-1 size-4 accent-primary cursor-pointer" />
              <span className="font-default text-xs text-muted-foreground leading-relaxed">
                I agree to receive community updates from Code Hub
              </span>
            </label>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      {submitError && <div className="border border-destructive/40 p-3">
          <p className="font-mono text-xs text-destructive">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="h-12 px-6 bg-primary text-primary-foreground hover:bg-primary/90 font-default text-xs font-semibold uppercase tracking-[0.18em] rounded-md w-full sm:w-auto">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Processing
          </> : <>Join Hub <span className="ml-2 inline-block size-2 rounded-md bg-foreground" /></>}
      </Button>
    </Form>;
}
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="space-y-3">
        {[0, 1, 2, 3].map(i => <li key={i} data-index={i} className="h-4 w-24 bg-muted animate-pulse rounded-md" />)}
      </ul>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i} className="group">
          <Link to={item.href || "#"} className="font-default text-sm text-foreground hover:text-primary transition-colors duration-200 inline-flex items-center gap-2">
            <span className="size-2 rounded-md bg-primary opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
const shopColumn = [{
  label: "Hackathons",
  href: "/CommunityPage"
}, {
  label: "Workshops",
  href: "/TheLabPage"
}, {
  label: "Projects",
  href: "/TheLabPage"
}, {
  label: "Hardware",
  href: "/TheLabPage"
}];
const customColumn = [{
  label: "Discord",
  href: "#"
}, {
  label: "Toolkit",
  href: "#"
}, {
  label: "Mentorship",
  href: "/JoinUsPage"
}, {
  label: "Events",
  href: "/CommunityPage"
}];
const contactColumn = [{
  label: "101 Tech Plaza",
  href: "#"
}, {
  label: "(555) 202-CODE",
  href: "tel:5552022633"
}, {
  label: "hello@codehub.io",
  href: "mailto:hello@codehub.io"
}, {
  label: "Open 24/7",
  href: "#"
}];
const socials = [{
  name: "Instagram",
  icon: Instagram,
  href: "#"
}, {
  name: "Facebook",
  icon: Facebook,
  href: "#"
}, {
  name: "Twitter",
  icon: Twitter,
  href: "#"
}, {
  name: "Youtube",
  icon: Youtube,
  href: "#"
}];
const legalLinks = [{
  label: "Privacy",
  href: "#"
}, {
  label: "Terms",
  href: "#"
}, {
  label: "Code of Conduct",
  href: "#"
}];
const tickerItems = ["CODE HUB", "BUILD. BREAK. REPEAT.", "OPEN SOURCE", "EST. 2024", "COMMUNITY DRIVEN"];
function MobileAccordionColumn({
  label,
  items,
  index
}: {
  label: string;
  items: {
    label: string;
    href: string;
  }[];
  index: number;
}) {
  const [open, setOpen] = useState(false);
  return <div data-index={index} className="border-b border-border">
      <Button variant="ghost" onClick={() => setOpen(!open)} className="w-full justify-between px-0 py-5 h-auto">
        <span className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
          {label}
        </span>
        {open ? <Minus className="size-4 text-primary" /> : <Plus className="size-4 text-muted-foreground" />}
      </Button>
      {open && <ul className="pb-5 space-y-3">
          {items.map((item, i) => <li key={item.label} data-index={i}>
              <Link to={item.href} className="font-default text-sm text-foreground hover:text-primary transition-colors duration-200">
                {item.label}
              </Link>
            </li>)}
        </ul>}
    </div>;
}
export default function Footer() {
  return <section data-section-id="2068" id="footer" className="bg-background text-foreground pt-24 pb-10 relative overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 pb-16">
          <div className="lg:col-span-9 flex flex-col justify-between">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground mb-6 flex items-center gap-3">
                <span>COMMAND / HUB_ROOT</span>
                <span className="h-px flex-1 bg-border max-w-24" />
                <span className="size-2 rounded-md bg-primary" />
              </p>
              <h2 className="font-default font-semibold text-foreground leading-[0.88] tracking-[-0.04em] select-none" style={{
              fontSize: "clamp(3rem, 12vw, 14rem)"
            }}>
                CODE<span className="italic text-primary">_</span>HUB
              </h2>
              <p className="mt-6 font-default text-sm text-muted-foreground max-w-md leading-relaxed">
                The premier community for developers, builders, and hardware enthusiasts. Join us to build the future, one commit at a time.
              </p>
            </div>
          </div>

          <div className="lg:col-span-3 lg:border-l lg:border-border lg:pl-8">
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">
              § DISPATCH / MONTHLY
            </p>
            <div className="h-px w-full bg-border mb-6 flex items-center">
              <span className="size-2 rounded-md bg-primary -ml-1" />
            </div>
            <h3 className="font-default font-semibold text-2xl text-foreground mb-2 leading-tight">
              Stay <span className="italic">connected</span>
            </h3>
            <p className="font-default text-sm text-muted-foreground mb-6 leading-relaxed">
              Get our open-source toolkit, event invites, and project highlights delivered monthly.
            </p>

            <FormProvider formKey="newsletter_signup_home" formId="newsletter_signup_home" sectionName="footer" defaultValues={{
            first_name: "",
            email: "",
            consent: false
          }} validationRules={{
            email: {
              required: true,
              pattern: "email"
            },
            consent: {
              required: true
            }
          }} submitText="Subscribe" successMessage="You're in. Check your terminal for the latest update." errorMessage="Connection failed. Please try again.">
              <NewsletterForm />
            </FormProvider>
          </div>
        </div>

        <div className="relative h-px w-full bg-border flex items-center">
          <span className="size-2 rounded-md bg-primary -ml-1" />
        </div>

        <div className="hidden md:grid md:grid-cols-4 gap-0 py-16 relative">
          <span className="hidden md:block absolute top-16 bottom-16 left-1/4 w-px bg-border" aria-hidden="true" />
          <span className="hidden md:block absolute top-16 bottom-16 left-2/4 w-px bg-border" aria-hidden="true" />
          <span className="hidden md:block absolute top-16 bottom-16 left-3/4 w-px bg-border" aria-hidden="true" />

          <div className="px-0 md:px-6 first:pl-0">
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground mb-6">
              § 01 — ACTIVITIES
            </p>
            <ul className="space-y-3">
              {shopColumn.map((item, i) => <li key={item.label} data-index={i} className="group">
                  <Link to={item.href} className="font-default text-sm text-foreground hover:text-primary transition-colors duration-200 inline-flex items-center gap-2">
                    <span className="size-2 rounded-md bg-primary opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    {item.label}
                  </Link>
                </li>)}
            </ul>
          </div>

          <div className="px-0 md:px-6">
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground mb-6">
              § 02 — RESOURCES
            </p>
            <ul className="space-y-3">
              {customColumn.map((item, i) => <li key={item.label} data-index={i} className="group">
                  <Link to={item.href} className="font-default text-sm text-foreground hover:text-primary transition-colors duration-200 inline-flex items-center gap-2">
                    <span className="size-2 rounded-md bg-primary opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    {item.label}
                  </Link>
                </li>)}
            </ul>
          </div>

          <div className="px-0 md:px-6">
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground mb-6">
              § 03 — COMMUNITY
            </p>
            <MenuProvider menu_id="34">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          <div className="px-0 md:px-6">
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground mb-6">
              § 04 — CONTACT
            </p>
            <ul className="space-y-3">
              {contactColumn.map((item, i) => <li key={item.label} data-index={i} className="group">
                  <Link to={item.href} className="font-default text-sm text-foreground hover:text-primary transition-colors duration-200 inline-flex items-center gap-2">
                    <span className="size-2 rounded-md bg-primary opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    {item.label}
                  </Link>
                </li>)}
            </ul>
          </div>
        </div>

        <div className="md:hidden py-2">
          <MobileAccordionColumn label="§ 01 — ACTIVITIES" items={shopColumn} index={0} />
          <MobileAccordionColumn label="§ 02 — RESOURCES" items={customColumn} index={1} />
          <MobileAccordionColumn label="§ 04 — CONTACT" items={contactColumn} index={3} />
        </div>
      </div>

      <div className="relative w-full border-y border-border py-5 overflow-hidden group">
        <div className="flex whitespace-nowrap animate-[scroll_40s_linear_infinite] group-hover:[animation-play-state:paused]">
          {[0, 1, 2, 3].map(dup => <div key={dup} data-index={dup} className="flex items-center shrink-0">
              {tickerItems.map((text, i) => <span key={`${dup}-${i}`} className="font-mono text-xs uppercase tracking-[0.18em] text-foreground flex items-center">
                  <span className="px-6">{text}</span>
                  <span className="size-2 rounded-md bg-primary" />
                </span>)}
            </div>)}
        </div>
        <style>{`
          @keyframes scroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
        `}</style>
      </div>

      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14 pt-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground text-center md:text-left">© 2026 CODE HUB. ALL RIGHTS RESERVED.</p>

          <div className="flex items-center justify-center gap-5">
            {socials.map((social, i) => {
            const Icon = social.icon;
            return <Link key={social.name} data-index={i} to={social.href} newTab aria-label={social.name} className="text-muted-foreground hover:text-primary transition-colors duration-200">
                  <Icon className="size-5" strokeWidth={1.25} />
                </Link>;
          })}
          </div>

          <div className="flex items-center justify-center md:justify-end gap-5">
            {legalLinks.map((link, i) => <Link key={link.label} data-index={i} to={link.href} className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-primary transition-colors duration-200">
                {link.label}
              </Link>)}
          </div>
        </div>
      </div>
    </section>;
}